import { isDefined } from '@sgme/fp';
import { batchWorkspaceApiConnector } from 'apiConnector/batch-workspace';
import { lastQuoteApi } from 'apiConnector/quote/lastquote';
import type { BatchFormResult } from 'models/forms/BatchFormResult';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { concat, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, timeoutWith, withLatestFrom } from 'rxjs/operators';
import {
  batchPreviousQuoteLoaded,
  defaultBatchFormFailed,
  defaultBatchFormTimeouted,
} from 'state/batchForm/batchForm.action';
import * as actionTypes from 'state/batchForm/batchForm.actionTypes';
import { defaultBatchFormReceivedThunk } from 'state/batchForm/batchForm.thunks';
import type { EpicWithThunk } from 'state/state.common';
import type { ImportFormRequest } from 'utils/import/import.models';

export interface BatchImportFormApi {
  importForm(request: ImportFormRequest): Observable<BatchFormResult>;
}

export function batchImportFormEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { frontLogger, httpClient }) =>
    action$.pipe(
      ofType(actionTypes.BATCH_IMPORT_FORM_REQUESTED),
      withLatestFrom(state$),
      mergeMap(([action, _state]) => {
        return batchWorkspaceApiConnector(httpClient)
          .importForm(action.request)
          .pipe(
            mergeMap(response => {
              const defaultBatchFormReceivedThunkAction = defaultBatchFormReceivedThunk(
                action.tabId,
                action.temporaryFormId,
                response.negotiationId,
                response.productView,
                response.actions,
                response.values,
                response.fieldsMetadata,
                response.quoteQuery,
                response.version,
                response.mustForceAdvancedMode ?? false,
                response.allowedRequests,
                response.errors,
              );

              const previousQuoteLoadedAction$ = isDefined(response.quoteId)
                ? lastQuoteApi(httpClient)
                    .getLastQuote(response.quoteId)
                    .pipe(
                      map(lastQuote =>
                        batchPreviousQuoteLoaded(response.negotiationId, response.quoteId!, lastQuote.quote),
                      ),
                      catchError(() => EMPTY),
                    )
                : EMPTY;

              return concat(of(defaultBatchFormReceivedThunkAction), previousQuoteLoadedAction$);
            }),
            catchError((error: Error) => {
              frontLogger.error('An error occurred during batch import form request {@FrontError}', error);
              return of(defaultBatchFormFailed(action.temporaryFormId));
            }),
            timeoutWith(timeoutInMillis, of(defaultBatchFormTimeouted(action.tabId)), appScheduler),
          );
      }),
    );
}
